import { useMemo, useState } from "react";
import { searchCustomer } from "src/apis/customerApi";
import { getItemPopupList } from "src/apis/itemApi";
import { SearchCodeModalForm } from "src/components/common/form/search/SearchCodeModalForm";
import { SearchDropDownForm } from "src/components/common/form/search/SearchDropDownForm";
import { SearchYearSingleForm } from "src/components/common/form/search/SearchYearSingleForm";
import { SearchSectionForm } from "src/components/common/form/search/SearchSectionForm";
import { SysCodeDto } from "src/dtos/code/sysCodeDto";
import { CompanyDto } from "src/dtos/company/companyDto";
import useUiuxStore from "src/store/useUiuxStore";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useSAL2050_R01Store, { defaultSAL2050_R01Filter } from "./Store_SAL2050_R01";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";

interface Props {}

export const SearchSection_SAL2050_R01 = ({}: Props) => {
	const {
		SAL2050_R01Filter,
		setSAL2050_R01Filter
	} = useSAL2050_R01Store();

	const {
		brandCdList,
		compCdList
	} = useCommonCodeStore();

	const { setCodeSearchModalProps } = useUiuxStore();

	const [yearVisible, setYearVisible] = useState(false);
	const [yearPos, setYearPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

	const yearValue = useMemo(() => {
		const salesYear = Number(SAL2050_R01Filter.salesYear);
		if (!Number.isFinite(salesYear) || salesYear <= 0) {
			return new Date(new Date().getFullYear(), 0, 1);
		}
		return new Date(salesYear, 0, 1);
	}, [SAL2050_R01Filter.salesYear]);

	const customCdHandleFoucsId = 'customCd_search';
	const itemCdHandleFoucsId = 'itemCd_search';

	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
		{
			fieldName: 'customCd',
			category: '거래처',
			focusId: customCdHandleFoucsId,
			responseMapping: {
				customCd: 'customCd',
				customNm: 'customNm',
			},
			searchFunction: async (searchText: string) => {
				const customerResponse = await searchCustomer(searchText);
				return customerResponse.data.data.find(value => value.customCd === searchText);
			},
			extraFilter: () => ({
				customCd: SAL2050_R01Filter.customCd || '',
				customNm: SAL2050_R01Filter.customNm || '',
			}),
		},
		{
			fieldName: 'itemCd',
			category: '품목',
			focusId: itemCdHandleFoucsId,
			responseMapping: {
				itemCd: 'itemCd',
				itemNm: 'itemNm',
			},
			searchFunction: async (searchText: string) => {
				const itemResponse = await getItemPopupList(searchText, '', '', '');
				return itemResponse.data.data.find(value => value.itemCd === searchText);
			},
			extraFilter: () => ({
				itemCd: SAL2050_R01Filter.itemCd || '',
				itemNm: SAL2050_R01Filter.itemNm || '',
			}),
		},
	];

	const { getFieldHandlers } = useInfoFormEvents({
		editData: SAL2050_R01Filter,
		setEditData: setSAL2050_R01Filter,
		defaultData: defaultSAL2050_R01Filter,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: 'SAL2050_R01_search'
	});

	return (
		<div className="w-full">
			<SearchSectionForm>
				<SearchDropDownForm<CompanyDto>
					label="사업장"
					value={SAL2050_R01Filter.compCd}
					optionList={compCdList}
					onChangeValue={v => setSAL2050_R01Filter({ compCd: v === 'all' ? '' : v })}
					wrapperOption={{
						wrapperStyle: "w-[200px] min-w-[200px] mr-[10px]",
						titleStyle: "w-[45px] min-w-[45px] text-left",
						selectStyle: "w-[100px] min-w-[100px]",
					}}
				/>
				<SearchYearSingleForm
					label="매출연도"
					visible={yearVisible}
					setVisible={setYearVisible}
					value={yearValue}
					onChange={(date) =>
						setSAL2050_R01Filter({
							salesYear: String((date ?? new Date()).getFullYear()),
						})
					}
					pickerPosition={yearPos}
					setPickerPosition={setYearPos}
				/>
				<SearchCodeModalForm
					label="거래처"
					value={SAL2050_R01Filter.customCd}
					valueLabel={SAL2050_R01Filter.customNm}
					handleFoucsId={customCdHandleFoucsId}
					onChange={getFieldHandlers('customCd').onChange}
					onDoubleClick={getFieldHandlers('customCd').onDoubleClick}
					onKeyDown={getFieldHandlers('customCd').onEnterKeyDown}
				/>
				<SearchDropDownForm<SysCodeDto>
					label="브랜드"
					value={SAL2050_R01Filter.brandCd}
					optionList={brandCdList}
					onChangeValue={(v) =>
						setSAL2050_R01Filter({
							brandCd: v === "all" ? "" : v,
						})
					}
					wrapperOption={{
						wrapperStyle: "w-[200px] min-w-[200px] mr-[10px]",
						titleStyle: "w-[45px] min-w-[45px] text-left",
						selectStyle: "w-[100px] min-w-[100px]",
					}}
				/>
				<SearchCodeModalForm
					label="품목코드"
					value={SAL2050_R01Filter.itemCd}
					valueLabel={SAL2050_R01Filter.itemNm}
					handleFoucsId={itemCdHandleFoucsId}
					onChange={getFieldHandlers('itemCd').onChange}
					onDoubleClick={getFieldHandlers('itemCd').onDoubleClick}
					onKeyDown={getFieldHandlers('itemCd').onEnterKeyDown}
				/>
			</SearchSectionForm>
		</div>
	);
};
